import React, { useEffect, useState } from "react";
import { convertEnergy2 } from "../../../../utils/dataAndFormulas/convertEnergy";
import { convertUnits } from "../../../../utils/dataAndFormulas/convert";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { formatWhole, formatMagnitude, formatMagnitudeAdaptive, formatPercentage } from "../../../../utils/numberFormat";
import "./ResumenEnergia.scss";

const formatEnergyAdaptive = (valueInMWh) => {
  if (valueInMWh < 1) {
    return {
      value: valueInMWh * 1000,
      unit: "kWh",
    };
  }

  return {
    value: valueInMWh,
    unit: "MWh",
  };
};

const formatByRule = (item) => {
  switch (item.rule) {
    case "whole":
      return formatWhole(item.value);
    case "adaptive":
      return formatMagnitudeAdaptive(item.value);
    case "magnitude":
    default:
      return formatMagnitude(item.value);
  }
};


const ResumenEnergia = ({ filters, site, valueBaseline, baselineLoaded = true, totales, selectorSlot, exportSlot }) => {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [data, setData] = useState({
    total: 0, // Total energía general (card 1)
    totalENE: 0, // Base ENE (eléctrica)
    renovablesSinIREC: 0,
    renovablesConIREC: 0,
    electricaNoRenovable: 0,
    electricaTotal: 0,
    renovablesElectricas: 0,
    intensidad: { valor: 0 },
    averageRenovables:0
  });

  /* ---------------- Helpers ---------------- */

  const getEnergyInMWh = (kpi, source) => {
    if (!kpi) return 0;
    return kpi.unit === "MWh"
      ? kpi.total
      : convertEnergy2(kpi.total, kpi.unit, "MWh", source, true); //utilizar mwh equi
  };

  const addData2 = (array) =>
    array.reduce((acc, k) => acc + getEnergyInMWh(k?.kpi, k?.source), 0);

  /* ---------------- useEffect ---------------- */

  useEffect(() => {
    if (!totales || !filters?.year) return;
    // Mientras se está cargando la línea base del sitio activo, `totales`
    // puede seguir siendo el del sitio anterior (recién se actualiza cuando
    // termina esta carga) mientras `valueBaseline` ya se reseteó a {} — si
    // se calculara igual, la intensidad daría total/1, es decir el valor
    // absoluto disfrazado de normalizado. Se espera a que termine.
    if (!baselineLoaded) return;

    // console.log(totales)

    /* ---------- TOTAL ENERGÍA (solo visual) ---------- */
    const total = getEnergyInMWh(
      totales?.total_energy_consumption_kpis?.[filters.year],
      "total_energy_consumption_kpis",
    );

    /* ---------- RENOVABLES DIRECTAS (ENE) ---------- */
    const directRenewableKeys = [
      "renewable_energies_ppa_combined",
      "renewable_energies_ppa_domestic",
      "renewable_energies_ppa_production",
      "renewable_energies_on_site_combined",
      "renewable_energies_on_site_domestic",
      "renewable_energies_on_site_production",
    ];

    const totalDirectRenewables = addData2(
      directRenewableKeys.map((k) => ({
        kpi: totales?.[k]?.[filters.year],
        source: k,
      })),
    );

    /* ---------- IREC ---------- */
    const irecCertificates = getEnergyInMWh(
      totales?.irec_certificates?.[filters.year],
      "irec_certificates",
    );

    /* ---------- RENOVABLES ---------- */
    const renovablesSinIREC = totalDirectRenewables;
    const renovablesConIREC = renovablesSinIREC + irecCertificates;
    const averageRenovables=totales?.renewable_energy_percentage_kpis?.[filters.year]?.total;

    /* ---------- ELECTRICIDAD TOTAL ---------- */
    const electricaTotal = getEnergyInMWh(
      totales?.total_electricity_kpis?.[filters.year],
      "total_electricity_kpis",
    );

    /* ---------- RENOVABLES ELÉCTRICAS (regla ENE) ---------- */
    const combinedKeys = [
      "renewable_energies_ppa_combined",
      "renewable_energies_on_site_combined",
    ];

    const domesticKeys = [
      "renewable_energies_ppa_domestic",
      "renewable_energies_on_site_domestic",
    ];

    const productionKeys = [
      "renewable_energies_ppa_production",
      "renewable_energies_on_site_production",
    ];

    const combinedElectric = combinedKeys.reduce(
      (acc, k) => acc + getEnergyInMWh(totales?.[k]?.[filters.year], k),
      0,
    );

    const renovablesElectricas =
      combinedElectric > 0
        ? combinedElectric
        : domesticKeys.reduce(
            (acc, k) => acc + getEnergyInMWh(totales?.[k]?.[filters.year], k),
            0,
          ) +
          productionKeys.reduce(
            (acc, k) => acc + getEnergyInMWh(totales?.[k]?.[filters.year], k),
            0,
          );

    /* ---------- ELECTRICIDAD NO RENOVABLE (ENE) ---------- */
    const electricaNoRenovable = Math.max(
      0,
      electricaTotal - renovablesElectricas,
    );

    /* ---------- TOTAL ENE (BASE KPIs) ---------- */
    const totalENE = renovablesConIREC + electricaNoRenovable;

    /* ---------- INTENSIDAD ---------- */
    const totalKWh = total * 1000; // intensidad usa energía total
    const unidadesBase = valueBaseline?.[filters.year] || 1;

    const intensidadValor = unidadesBase > 0 ? totalKWh / unidadesBase : 0;

    setData({
      total,
      totalENE,
      renovablesSinIREC,
      renovablesConIREC,
      electricaNoRenovable,
      electricaTotal,
      renovablesElectricas,
      intensidad: { valor: intensidadValor },
      averageRenovables:averageRenovables
    });
  }, [filters, site, valueBaseline, totales, baselineLoaded]);

  /* ---------------- PORCENTAJES ENE ---------------- */

  const percentRenovableConIREC =
    data.totalENE > 0 ? (data.renovablesConIREC / data.totalENE) * 100 : 0;

  const percentRenovableSinIREC =
    data.totalENE > 0
      ? (data.renovablesSinIREC / data.total) * 100 //(ENERGIA ELECTRICA RENOVABLE SIN IREC TOTAL /ENERGIA TOTAL)
      : 0;

  const percentElectrica =
    data.totalENE > 0
      ? (data.electricaTotal / data.total) * 100 //(ENERGIA ELECTRICA TOTAL / ENERGIA TOTAL)
      : 0;
 
  /* ---------------- FORMATEO ---------------- */

  const totalFmt = formatEnergyAdaptive(data.total);
  const renovablesFmt = formatEnergyAdaptive(data.renovablesSinIREC);
  const electricaFmt = formatEnergyAdaptive(data.electricaTotal);

  /* ---------------- ITEMS (NO TOCAR UI) ---------------- */

  const resumenItems = [
    {
      title: t("total_energy"),
      ...totalFmt,
      unit: `${totalFmt.unit}e`,
      rule: "whole",
      hasProgress: false,
    },
    {
      title: t("renewables"),
      ...renovablesFmt,
      rule: "whole",
      secondary: formatPercentage(data.averageRenovables),
      progress: data.averageRenovables,
      hasProgress: true,
    },
    {
      title: t("electricity"),
      ...electricaFmt,
      rule: "whole",
      secondary: formatPercentage(percentElectrica),
      progress: percentElectrica,
      hasProgress: true,
    },
    {
      title: t("intensity"),
      value: data.intensidad.valor,
      unit: "kWh/Unidad",
      rule: "magnitude",
      hasProgress: false,
    },
  ];

  /* ---------------- RENDER ---------------- */

  return (
    <div className="resumen-grid">
      {resumenItems.map((item, index) => (
        <div
          key={index}
          className={`resumen-card-col${index === 0 ? " resumen-card-col--first" : ""}`}
        >
          <div className={`resumen-slot-row${index === resumenItems.length - 1 ? " resumen-slot-row--right" : ""}`}>
            {index === 0 && selectorSlot}
            {index === resumenItems.length - 1 && exportSlot}
          </div>
        <div
          className={`resumen-card energy-card ${
            !item.hasProgress ? "no-progress" : ""
          }`}
        >
          <div className="card-meta">
            <div className="card-title-row">
              <span className="card-title">{item.title}</span>
            </div>
            <div>
              {item.subtitle && (
                <span className="card-subtitle">{item.subtitle}</span>
              )}
            </div>
          </div>

          <div className="card-header">
            <span className="card-value">
              {formatByRule(item)}
              <span className="card-unit">{item.unit}</span>
            </span>
            {item.secondary && (
              <div className="percentage-text">{item.secondary}</div>
            )}
          </div>

          {item.hasProgress && (
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${Math.round(item.progress || 0)}%`,
                    backgroundColor: item.color || "blue",
                  }}
                />
              </div>
            </div>
          )}

          <div className="empty-space"></div>
        </div>
        </div>
      ))}
    </div>
  );
};

export default ResumenEnergia;
